import { Component, ChangeDetectionStrategy, computed, input, signal } from '@angular/core';

/** Une série d'une barre empilée (réglé, en vérification, dû…). */
export interface SerieBarres { nom: string; couleur: string; }

/**
 * Une barre. `valeurs` suit l'ordre des séries. `couleur` remplace celle de la série
 * unique quand la ligne elle-même porte l'identité (frais club / hors club).
 */
export interface LigneBarres { cle: string; label: string; valeurs: number[]; couleur?: string; }

/**
 * Barres horizontales, empilées si plusieurs séries — pour des catégories nombreuses ou aux
 * libellés longs (types de frais, sources de recettes). Libellé à gauche, total à droite,
 * 2 px de fond entre segments, bout arrondi côté valeur. Survol : détail par série.
 */
@Component({
  selector: 'app-barres-h',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (legende().length > 1) {
      <div class="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3 text-xs text-stone-500">
        @for (l of legende(); track l.nom) {
          <span class="flex items-center gap-1.5"><span class="w-2.5 h-2.5 rounded-sm" [style.background-color]="l.couleur"></span>{{ l.nom }}</span>
        }
      </div>
    }
    <div class="space-y-2.5">
      @for (l of lignes(); track l.cle; let i = $index) {
        <div class="relative" (mouseenter)="survol.set(i)" (mouseleave)="survol.set(null)">
          <div class="flex items-center gap-3">
            <span class="w-32 sm:w-40 flex-shrink-0 truncate text-sm text-stone-600" [title]="l.label">{{ l.label }}</span>
            <div class="flex-1 min-w-0 h-3.5 flex gap-[2px]">
              <div class="flex h-full gap-[2px] rounded-r-[4px] overflow-hidden" [style.width.%]="largeur(l)">
                @for (v of l.valeurs; track $index; let s = $index) {
                  @if (v > 0) {
                    <div class="h-full" [style.flex-grow]="v" [style.background-color]="couleur(l, s)"></div>
                  }
                }
              </div>
            </div>
            <span class="w-24 flex-shrink-0 text-right text-sm font-medium text-stone-800 tabular-nums">{{ somme(l) | number:'1.0-0' }}</span>
          </div>

          @if (survol() === i && series().length > 1) {
            <div class="absolute z-10 right-24 bottom-full mb-1 pointer-events-none bg-white border border-stone-200 shadow-lg rounded-xl px-3 py-2 text-xs min-w-[170px]">
              <p class="font-semibold text-stone-700 mb-1">{{ l.label }}</p>
              @for (s of series(); track s.nom; let k = $index) {
                <p class="flex items-center gap-2 whitespace-nowrap text-stone-600">
                  <span class="w-2 h-2 rounded-sm flex-shrink-0" [style.background-color]="s.couleur"></span>
                  <span class="flex-1">{{ s.nom }}</span>
                  <span class="font-medium text-stone-800 tabular-nums">{{ l.valeurs[k] | number:'1.2-2' }}</span>
                </p>
              }
            </div>
          }
        </div>
      }
    </div>
  `,
})
export class BarresHComponent {
  series = input.required<SerieBarres[]>();
  lignes = input.required<LigneBarres[]>();
  /** Légende imposée (lignes colorées par famille) ; sinon celle des séries. */
  legendeFamilles = input<SerieBarres[] | null>(null);

  survol = signal<number | null>(null);

  legende = computed(() => this.legendeFamilles() ?? this.series());

  private max = computed(() => Math.max(0, ...this.lignes().map(l => this.somme(l))));

  somme(l: LigneBarres): number {
    return l.valeurs.reduce((t, v) => t + (v > 0 ? v : 0), 0);
  }

  largeur(l: LigneBarres): number {
    const m = this.max();
    return m > 0 ? Math.max(1, (this.somme(l) / m) * 100) : 0;
  }

  couleur(l: LigneBarres, s: number): string {
    return l.couleur ?? this.series()[s]?.couleur ?? '#a8a29e';
  }
}
